
import { useState, useRef, useEffect } from "react";
import { Mic, Check, X, Loader2, Sparkles, AlertCircle } from "lucide-react";
import clsx from "clsx";
import { motion, AnimatePresence } from "framer-motion";
import { DailyLog } from "@/types";

interface VoiceParserProps {
    onSave: (partialLog: Partial<DailyLog>) => void;
    onCancel: () => void;
    placeholder?: string;
    variant?: "modal" | "section";
}

export default function VoiceParser({ onSave, onCancel, placeholder, variant = "modal" }: VoiceParserProps) {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [status, setStatus] = useState<"idle" | "listening" | "processing" | "review" | "error">("idle");
    const [parsedData, setParsedData] = useState<any>(null);
    const [errorMsg, setErrorMsg] = useState("");
    const recognitionRef = useRef<any>(null);

    useEffect(() => {
        // Initialize Speech Recognition
        if (typeof window !== "undefined") {
            const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
            if (SpeechRecognition) {
                recognitionRef.current = new SpeechRecognition();
                recognitionRef.current.continuous = true;
                recognitionRef.current.interimResults = true;
                recognitionRef.current.lang = "en-US";

                recognitionRef.current.onresult = (event: any) => {
                    let currentTranscript = "";
                    for (let i = 0; i < event.results.length; i++) {
                        currentTranscript += event.results[i][0].transcript;
                    }
                    setTranscript(currentTranscript);
                };

                recognitionRef.current.onerror = (event: any) => {
                    console.error("Speech recognition error", event.error);
                    setStatus("error");
                    let msg = "Microphone error: " + event.error;
                    if (event.error === 'network') {
                        msg = "Network error: Speech recognition requires internet connection.";
                    } else if (event.error === 'not-allowed') {
                        msg = "Microphone access denied. Please allow permission.";
                    } else if (event.error === 'no-speech') {
                        msg = "No speech detected. Please try again.";
                    }
                    setErrorMsg(msg);
                    setIsListening(false);
                };

                recognitionRef.current.onend = () => {
                    if (status === "listening") {
                        setIsListening(false); // Only auto-stop visual if intended
                    }
                };
            } else {
                setErrorMsg("Speech recognition not supported in this browser.");
                setStatus("error");
            }
        }
    }, []);

    const toggleListening = () => {
        if (!recognitionRef.current) return;

        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
            if (transcript.trim().length > 0) {
                processTranscript();
            } else {
                setStatus("idle");
            }
        } else {
            setTranscript("");
            setErrorMsg("");
            setStatus("listening");
            recognitionRef.current.start();
            setIsListening(true);
        }
    };


    const processTranscript = async () => {
        if (!transcript.trim()) return;

        setStatus("processing");
        setIsProcessing(true);

        try {
            const res = await fetch('/api/ai-coach', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mode: 'parse_log',
                    text: transcript
                })
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.error || `Failed to process speech (${res.status})`);
            }

            const data = await res.json();

            // Success - show review
            setStatus("review");
            setParsedData(data); // Store data for review
            setIsProcessing(false);

        } catch (err: any) {
            console.error(err);
            setStatus("error");
            setErrorMsg(err.message || "Failed to parse log.");
            setIsProcessing(false);
        }
    };

    const handleConfirm = () => {
        onSave(parsedData);
    };

    return (
        <div className={clsx(
            "flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 transition-all duration-300",
            variant === "modal" ? "fixed inset-0 z-[9999]" : "absolute inset-0 z-50 rounded-2xl"
        )}>
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="w-full max-w-sm bg-[#0f172a] border border-white/10 rounded-3xl shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]"
            >
                {/* Background Glow */}
                <div className={clsx(
                    "absolute top-0 right-0 w-64 h-64 bg-primary/20 blur-[80px] rounded-full -translate-y-1/2 translate-x-1/2 transition-all duration-700 pointer-events-none",
                    isListening ? "opacity-100 scale-125 bg-red-500/20" : "opacity-0 scale-100"
                )}></div>

                <div className="relative z-10 flex flex-col h-full">

                    {/* Header */}
                    <div className="p-6 pb-0 flex justify-between items-center">
                        <h2 className="text-lg font-bold text-white tracking-wide">
                            {status === "review" ? "Review Entry" : "AI Voice Log"}
                        </h2>
                        <button onClick={onCancel} className="p-2 text-text-muted hover:text-white bg-white/5 rounded-full">
                            <X size={20} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">

                        {/* Transcript Area - Editable */}
                        {(status === "idle" || status === "listening" || status === "processing" || status === "error") && (
                            <div className="w-full flex-col gap-4">
                                <div className="w-full min-h-[120px] bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col items-center justify-center relative transition-colors focus-within:bg-white/10 focus-within:border-white/20">
                                    <textarea
                                        value={transcript}
                                        onChange={(e) => setTranscript(e.target.value)}
                                        placeholder={status === "listening" ? "Listening..." : (placeholder || "Tap mic or type here...")}
                                        className="w-full h-full bg-transparent border-none outline-none text-white text-lg font-medium leading-relaxed resize-none text-center placeholder:text-text-muted/50"
                                        rows={3}
                                    />

                                    {status === "error" && (
                                        <div className="flex items-center gap-2 text-rose-400 text-sm mt-2">
                                            <AlertCircle size={14} /> {errorMsg}
                                        </div>
                                    )}
                                </div>

                                {/* Manual Analyze Button (if text exists and not listening) */}
                                {transcript.length > 2 && !isListening && status !== "processing" && (
                                    <button
                                        onClick={processTranscript}
                                        className="w-full mt-4 py-3 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-white font-bold flex items-center justify-center gap-2 transition-all"
                                    >
                                        <Sparkles size={18} className="text-primary" /> Analyze Text
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Mic Controls (Hidden in Review) */}
                        {status !== "review" && (
                            <div className="flex flex-col items-center gap-4 w-full mt-4">
                                <button
                                    onClick={toggleListening}
                                    disabled={status === "processing"}
                                    className={clsx(
                                        "w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg",
                                        isListening
                                            ? "bg-red-500 shadow-red-500/50 scale-110 animate-pulse"
                                            : status === "processing"
                                                ? "bg-amber-500/20 cursor-wait border border-amber-500/50"
                                                : "bg-white/10 hover:bg-white/20 border border-white/10 text-white"
                                    )}
                                >
                                    {status === "processing" ? (
                                        <Loader2 size={24} className="text-amber-400 animate-spin" />
                                    ) : isListening ? (
                                        <div className="w-6 h-6 rounded-sm bg-white" />
                                    ) : (
                                        <Mic size={24} />
                                    )}
                                </button>
                            </div>
                        )}

                        {/* Review UI */}
                        {status === "review" && parsedData && (
                            <div className="space-y-4">
                                {/* Transcript Recap */}
                                <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                                    <span className="text-[10px] uppercase text-text-muted font-bold tracking-wider mb-1 block">Transcript</span>
                                    <p className="text-sm text-gray-300 italic">"{transcript}"</p>
                                </div>

                                {/* Extracted Data Grid */}
                                <div className="grid grid-cols-2 gap-2">
                                    {parsedData.meals && parsedData.meals.length > 0 && (
                                        <div className="col-span-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                                            <span className="text-[10px] uppercase text-blue-400 font-bold tracking-wider mb-2 block">Food</span>
                                            <div className="space-y-1">
                                                {parsedData.meals.map((m: any, i: number) => (
                                                    m.items.map((item: any, j: number) => (
                                                        <div key={`${i}-${j}`} className="flex justify-between text-xs text-blue-200">
                                                            <span>{item.quantity}x {item.name} <span className="text-blue-400/50">({m.name})</span></span>
                                                            <span>{item.calories} kcal</span>
                                                        </div>
                                                    ))
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Workout */}
                                    {parsedData.workout && parsedData.workout.type !== "Rest" && (
                                        <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-xl">
                                            <span className="text-[10px] uppercase text-orange-400 font-bold tracking-wider mb-1 block">Workout</span>
                                            <div className="text-xs text-orange-200">
                                                <div className="font-bold">{parsedData.workout.type}</div>
                                                <div>{parsedData.workout.duration} min • {parsedData.workout.calories} kcal</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Stats Grid */}
                                    {parsedData.sleep > 0 && (
                                        <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
                                            <span className="text-[10px] uppercase text-indigo-400 font-bold tracking-wider mb-1 block">Sleep</span>
                                            <div className="text-lg font-bold text-indigo-200">{parsedData.sleep} <span className="text-xs font-normal">hrs</span></div>
                                        </div>
                                    )}

                                    {parsedData.water > 0 && (
                                        <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
                                            <span className="text-[10px] uppercase text-cyan-400 font-bold tracking-wider mb-1 block">Water</span>
                                            <div className="text-lg font-bold text-cyan-200">{parsedData.water} <span className="text-xs font-normal">L</span></div>
                                        </div>
                                    )}

                                    {/* Bad Habits */}
                                    {(parsedData.alcohol?.units > 0 || parsedData.smoking?.quantity > 0) && (
                                        <div className="col-span-2 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl flex gap-4">
                                            {parsedData.alcohol?.units > 0 && (
                                                <div>
                                                    <span className="text-[10px] uppercase text-rose-400 font-bold tracking-wider mb-1 block">Alcohol</span>
                                                    <div className="text-xs text-rose-200">{parsedData.alcohol.type}: {parsedData.alcohol.units} units</div>
                                                </div>
                                            )}
                                            {parsedData.smoking?.quantity > 0 && (
                                                <div>
                                                    <span className="text-[10px] uppercase text-rose-400 font-bold tracking-wider mb-1 block">Smoking</span>
                                                    <div className="text-xs text-rose-200">{parsedData.smoking.quantity} cigs</div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Supplements */}
                                    {parsedData.supplements && Object.keys(parsedData.supplements).length > 0 && (
                                        <div className="col-span-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                                            <span className="text-[10px] uppercase text-emerald-400 font-bold tracking-wider mb-1 block">Supplements</span>
                                            <div className="flex flex-wrap gap-1">
                                                {Object.keys(parsedData.supplements).map(s => (
                                                    <span key={s} className="px-1.5 py-0.5 rounded-md bg-emerald-500/20 text-[10px] text-emerald-300 border border-emerald-500/30">{s}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>

                    {/* Footer Actions */}
                    <div className="p-6 pt-2">
                        {transcript && !isListening && status !== "processing" && status !== "review" && (
                            <button
                                onClick={processTranscript}
                                className="w-full py-3 bg-white text-black rounded-xl font-bold text-sm hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                            >
                                <Sparkles size={16} /> Analyze
                            </button>
                        )}

                        {status === "review" && (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => { setStatus("idle"); setTranscript(""); setParsedData(null); }}
                                    className="flex-1 py-3 bg-white/5 text-text-muted rounded-xl font-bold text-sm hover:bg-white/10 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    className="flex-[2] py-3 bg-green-500 text-black rounded-xl font-bold text-sm hover:bg-green-400 transition-colors flex items-center justify-center gap-2"
                                >
                                    <Check size={16} /> Confirm & Save Log
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
